import { Component, Element, h, Host, Listen, Prop, State } from "@stencil/core";
import { AppStateType, AppStoreType, buildEvent } from "../../utils/utils";
import { format } from "timeago.js";
import { getItemTabIndex, isWidgetDisabled } from "../../store/getters";
import { setSliderIndex, setItemInFocus } from "../../store/slider";
import { StarRating } from "../../utils/funcComponents";
import { Settings, Unsubscribe } from "../../utils/types";
import { standardRatio } from "../covet-pics-crop-text/crop-settings";

@Component({
  tag: "covet-pics-gallery-item",
  styleUrl: "covet-pics-gallery-item.scss"
})

export class CovetPicsGalleryItem {
  @Element() el: HTMLCovetPicsGalleryItemElement;

  /**
   * image ALT tag
   */
  @Prop() altTag: string = "";

  /**
   * if item is highlighted
   */
  @Prop() highlighted: boolean = false;

  /**
   * URL to the image that will be displayed
   */
  @Prop() imageUrl: string;

  /**
   * Star rating
   */
  @Prop() rating: number = 0;

  /**
  * item width (different size settings, breakpoint for item width, 160px, 240px, 480px)
 */
  @Prop() itemWidth: number = 320;

  /**
   * User name
   */
  @Prop() userName: string;

  /**
   * Created time
  */
  @Prop() createdTime: string;

  /**
  * Caption text (comment)
 */
  @Prop() caption: string = "";

  /**
   * item type (image, video, carousel, text)
  */
  @Prop() itemType: string;

  /**
   * URL to the hi-res. image that will be displayed
   */
  @Prop() imageHighResolutionUrl: string;

  /**
   * responsive sizes settings - hint for browser to load proper resolution image
   */
  @Prop() imageResponsiveSizes: string;

  /**
   * Unique `ID`, this value is used to open `CovetPopup`
   */
  @Prop() itemId: string;

  /**
   * Where the item is coming from (`instagram`, `upload_widget`, `file`)
   */
  @Prop() itemSource: string = "instagram";

  /**
   * links for this post (can be product or general link)
   */
  @Prop() links: Array<any>;

  /**
   * Link to Instagram post
   */
  @Prop() url: string;

  /**
   * item index in slider
   */
  @Prop() itemIndex: number;

  /**
   * item size (size-xs, size-sm, size-md, size-lg)
   */
  @Prop() itemSize: string = "size-md";

  /**
   * App store
   */
  @Prop() appStore: AppStoreType;

  // App state;
  private appState: AppStateType;
  private unsubscribe: Unsubscribe;
  private hoverAnimationStyle: string; // TODO short vars name
  private labelCaption: string; // TODO short vars name 
  private settings: Settings;

  @State() tabIndex: number = 0;

  @Listen("updateUploadWidgetCover", { capture: true, target: "window" })
  updateUploadWidgetCoverHandler(event) {
    if (this.itemSource === "upload_widget") {
      const previewImage = event.detail.image;

      if (previewImage === null) {
        this.imageUrl = undefined;
      } else {
        this.imageUrl = previewImage;
      }
    }
  }

  componentWillLoad() {
    if (this.appStore !== undefined) { this.initStore(); } // TODO temp check for pass spec.test, issue props function in stencil tests
  }

  disconnectedCallback() {
    this.unsubscribe();
  }

  private nextTick = (fn) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(fn);
    });
  }

  private initStore() {
    this.appState = this.appStore.getState();
    this.settings = this.appState.settings;
    // TODO refactor short var
    this.hoverAnimationStyle = this.settings.item_animation_type;
    this.labelCaption = (this.links === undefined || this.links.length === 0)
      ? this.settings.item_without_product_label_text
      : this.settings.item_label_text;
    // TODO refactor short var END
    this.unsubscribe = this.appStore.subscribe(() => {
      const isModalsOpen = isWidgetDisabled(this.appState);
      const isModalsWillClose = isModalsOpen && !isWidgetDisabled(this.appStore.getState());
      const isItemHoverChange = this.appState.slider.itemInFocus !== this.appStore.getState().slider.itemInFocus;
      this.appState = this.appStore.getState();
      this.settings = this.appState.settings;
      this.tabIndex = getItemTabIndex(this.appState, this.itemIndex); // TODO update function
      if (isModalsWillClose && this.appState.slider.realIndex === this.itemIndex) {
        this.nextTick(() =>
          this.itemSource === "upload_widget" ? this.el.focus() : (this.el.querySelector(".btn-item-wrap") as HTMLButtonElement).focus()
        );
      }
      if (isItemHoverChange && this.itemIndex !== this.appState.slider.itemInFocus) {
        this.itemSource === "upload_widget" ? this.el.blur() : (this.el.querySelector(".btn-item-wrap") as HTMLButtonElement).blur();
      }
    });
  }

  private get hoverEnabled() {
    return !this.settings.item_hover_disabled && (this.settings.item_open_link || !this.settings.popup_disable_modal);
  }

  private get animationDisabled() {
    return this.settings.item_animation_duration === 0;
  }

  private imageLoadedHandler = () => {
    const customEvent = buildEvent("imageLoaded");

    this.el.removeAttribute("is-loading");
    this.el.dispatchEvent(customEvent);
  }

  private itemClickHandler = (event) => {
    if (event.type === "keydown" && event.keyCode !== 13 && event.keyCode !== 32) return false;
    if (this.settings.popup_disable_modal) {
      if (!this.settings.item_open_link) { return false; }

      if (this.links.length > 0) {
        const link = this.links[0];
        const context = link.new_tab ? "_blank" : "_self";
        window.open(link.link, context);
      } else if (typeof this.url !== "undefined" && this.itemSource === "instagram") {
        window.open(this.url, "_top");
      }
    } else {
      const customEvent = buildEvent("itemClicked", this);
      this.el.dispatchEvent(customEvent);
      event.preventDefault();
    }
  }

  private itemOnFocusHandler = () => {
    return ["grid", "collage"].includes(this.appState.settings.display_type)
      ? this.appStore.dispatch(setSliderIndex(this.itemIndex))
      : true;
  }

  private itemOnHoverHandler = () => {
    return this.appStore.dispatch(setItemInFocus(this.itemIndex));
  }

  private getLabelCaption() {
    if (this.settings.popup_disable_modal && !this.settings.item_open_link) return null;
    if (this.links && this.links.length > 0) return this.settings.item_label_text;

    return this.labelCaption;
  }

  private getItemCSSVariables() {
    let baseAnimationSpeed = 0.5; // range value from 0.5s to 1s depends of animation duration
    if (Math.round(this.settings.item_animation_duration) === 0) {
      baseAnimationSpeed = 0;
    }
    if (Math.round(this.settings.item_animation_duration) > 1) {
      baseAnimationSpeed = baseAnimationSpeed + Math.round(this.settings.item_animation_duration) / 10;
    }
    return {
      "--covet-pics-item-hover-background-opacity": (this.settings.item_hover_background_opacity / 100).toString(),
      "--covet-pics-item-hover-color-from": this.settings.item_hover_color_from,
      "--covet-pics-item-hover-color-to": this.settings.item_hover_color_to,
      "--covet-pics-item-hover-border-color": this.settings.item_hover_border_color,
      "--covet-pics-item-label-color": this.settings.item_label_color,
      // TODO: change fontStyle to fontWeight
      "--covet-pics-item-label-font-style": this.settings.item_label_font_style.toLowerCase(),
      "--covet-pics-item-label-font-size": this.settings.item_label_font_size + "px",
      "--covet-pics-item-animation-duration": this.settings.item_animation_duration.toString() + "s",
      "--covet-pics-item-animation-duration-base": `${baseAnimationSpeed}s`
    };
  }

  private renderInstagramIcon(animationStyles: Array<string>) {
    if (this.settings.item_hover_icon && this.itemSource === "instagram" && animationStyles.includes(this.hoverAnimationStyle)) {
      return <svg class="item-open-icon" viewBox="0 0 56.7 56.7" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><g><path class="dynamic dynamic-fill" d="M28.2,16.7c-7,0-12.8,5.7-12.8,12.8s5.7,12.8,12.8,12.8S41,36.5,41,29.5S35.2,16.7,28.2,16.7z M28.2,37.7   c-4.5,0-8.2-3.7-8.2-8.2s3.7-8.2,8.2-8.2s8.2,3.7,8.2,8.2S32.7,37.7,28.2,37.7z"></path><circle class="dynamic dynamic-fill" cx="41.5" cy="16.4" r="2.9"></circle><path class="dynamic dynamic-fill" d="M49,8.9c-2.6-2.7-6.3-4.1-10.5-4.1H17.9c-8.7,0-14.5,5.8-14.5,14.5v20.5c0,4.3,1.4,8,4.2,10.7c2.7,2.6,6.3,3.9,10.4,3.9   h20.4c4.3,0,7.9-1.4,10.5-3.9c2.7-2.6,4.1-6.3,4.1-10.6V19.3C53,15.1,51.6,11.5,49,8.9z M48.4,39.9c0,3.1-0.9,5.6-2.7,7.3   c-1.8,1.7-4.3,2.6-7.3,2.6H18c-3,0-5.5-0.9-7.3-2.6C8.9,45.4,8,42.9,8,39.8V19.3c0-3,0.9-5.5,2.7-7.3c1.7-1.7,4.3-2.6,7.3-2.6h20.6   c3,0,5.5,0.9,7.3,2.7c1.7,1.8,2.7,4.3,2.7,7.2V39.9z"></path></g></svg>;
    }
  }

  private renderRating(additionalClass: string = "") {
    return (
      <div class={`rating ${this.itemSize} ${additionalClass}`}>
        <span class={`icon-star active ${this.itemSize} ${additionalClass}`}>
          <svg class={`svg-star ${additionalClass}`} viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" role="img"><title>star rating</title>
            <path d="M4.10511 3.85963C4.10511 3.85963 1.83465 4.12327 0.320057 4.29953C0.183543 4.31685 0.0628347 4.41251 0.0175692 4.55789C-0.0276963 4.70327 0.0175691 4.85467 0.118159 4.95033C1.24333 6.02672 2.93396 7.63867 2.93396 7.63867C2.93252 7.63867 2.46981 9.98349 2.16229 11.548C2.13642 11.6896 2.18529 11.8395 2.30312 11.9291C2.42023 12.0188 2.57112 12.0203 2.68967 11.9503C4.01458 11.1609 5.99907 9.97371 5.99907 9.97371C5.99907 9.97371 7.98429 11.1609 9.30703 11.951C9.42772 12.0203 9.57866 12.0188 9.69574 11.9291C9.8136 11.8395 9.86246 11.6896 9.83589 11.5488C9.52834 9.98349 9.06634 7.63867 9.06634 7.63867C9.06634 7.63867 10.757 6.02672 11.8821 4.95259C11.9828 4.85391 12.0273 4.70252 11.9828 4.55789C11.9382 4.41327 11.8175 4.31761 11.681 4.30103C10.1664 4.12327 7.8952 3.85963 7.8952 3.85963C7.8952 3.85963 6.95397 1.67673 6.32671 0.221454C6.2678 0.0911426 6.14422 0 5.99907 0C5.85394 0 5.72964 0.091896 5.67359 0.221454C5.04563 1.67673 4.10511 3.85963 4.10511 3.85963Z" />
          </svg>
        </span>
        <span class={`rating-data ${additionalClass}`}>{this.rating}</span>
      </div>
    );
  }

  private renderUserAndDate() {
    const textReviewClass = this.caption !== null && this.itemType === "text" ? "text-review" : "";
    const starOnlyClass = this.caption === null && this.itemType === "text" ? "star-only" : "";
    const hoverDisabledClass = this.settings.item_hover_disabled ? "hover-disabled" : "";
    return (
      <div class={`name-data ${textReviewClass} ${starOnlyClass} ${this.itemSize}`}>
        <h3 class={`username ${textReviewClass} ${starOnlyClass} ${hoverDisabledClass}`}>{this.userName}</h3>
        {this.userName && this.createdTime && <span class={`slash ${textReviewClass} ${starOnlyClass} ${hoverDisabledClass}`}>/</span>}
        <span class={`created-time ${textReviewClass} ${starOnlyClass} ${hoverDisabledClass}`}>{format(this.createdTime)}</span>
      </div>
    );
  }

  private renderStandardItem() {
    const starRatingClass = this.rating > 0 ? "star-rating" : "";
    return (
      <Host class={{ [`${this.hoverAnimationStyle} hover-animation`]: this.hoverEnabled, "hover-preview": this.appState.preview.hoverMode, "no-animation": this.animationDisabled, "highlighted": this.highlighted }}
        style={this.getItemCSSVariables()} is-loading aria-hidden={`${this.tabIndex === -1}`}>
        <button class="btn-item-wrap" disabled={this.tabIndex === -1} onFocus={this.itemOnFocusHandler} onMouseEnter={this.itemOnHoverHandler} 
          onKeyDown={this.itemClickHandler} onClick={this.itemClickHandler} type="button" aria-label={this.getLabelCaption()}>
          {this.renderInstagramIcon(["hover-animation-2", "hover-animation-4"])}
          {this.imageUrl && this.imageHighResolutionUrl &&
          <img class="bg"
            onLoad={this.imageLoadedHandler} onError={this.imageLoadedHandler}
            srcset={`${this.imageUrl} 640w, ${this.imageHighResolutionUrl} 1080w`}
            sizes={this.imageResponsiveSizes}
            alt={this.altTag}
            loading="lazy" />
          }
          {(this.hoverAnimationStyle === "hover-animation-1" || this.hoverAnimationStyle === "hover-animation-2") && this.rating > 0 && this.renderRating()}
          <span class="overlay-effect"></span>
          {this.hoverAnimationStyle === "hover-animation-5" && this.rating > 0 &&
            <StarRating rating={this.rating} ratingClass={`rating ${this.itemSize}`} label={`${this.rating} / 5`}></StarRating>
          }
          <span class={`info hover ${this.itemSize}`}>
            {this.renderInstagramIcon(["hover-animation-1", "hover-animation-5"])}
            {/* TODO && "show" */}
            <span class={`btn source-${this.itemSource} ${this.settings.item_hover_icon && "show"} ${starRatingClass}`}>
              {this.renderInstagramIcon(["hover-animation-3"])}

              {this.hoverAnimationStyle === "hover-animation-2" &&
              <svg width="20" height="20" xmlns="http://www.w3.org/2000/svg" class="icon-arrow" aria-hidden="true"><path class="dynamic-fill" d="M13.3535534,4 L19.7071068,10.3535534 L13.3535534,16.7071068 L12.6464466,16 L17.792,10.853 L1,10.8535534 L1,9.85355339 L17.792,9.853 L12.6464466,4.70710678 L13.3535534,4 Z" fill="#ffffff" fill-rule="nonzero"></path></svg>
              }

              {this.getLabelCaption()}

              {this.hoverAnimationStyle === "hover-animation-4" && this.rating === 0 &&
              <svg width="20px" height="20px" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><polygon fill="#fff" class="dynamic-fill" points="6 4.70710678 6.70710678 4 13.0606602 10.3535534 6.70710678 16.7071068 6 16 11.6464466 10.3535534"></polygon></svg>
              }
              {(this.hoverAnimationStyle === "hover-animation-3" || this.hoverAnimationStyle === "hover-animation-4") && this.rating > 0 && this.renderRating("info-rating")}
            </span>
          </span>
          {(this.hoverAnimationStyle === "hover-animation-1" || this.hoverAnimationStyle === "hover-animation-2") && this.rating > 0 && this.renderUserAndDate()}
        </button>
      </Host>
    );
  }

  private renderDisabledHoverStandardItem() {
    return (
      <Host class={{ "no-animation": this.settings.item_hover_disabled, "highlighted": this.highlighted }}
        style={this.getItemCSSVariables()} is-loading aria-hidden={`${this.tabIndex === -1}`}>
        <button class="btn-item-wrap" disabled={this.tabIndex === -1} onFocus={this.itemOnFocusHandler} onMouseEnter={this.itemOnHoverHandler} 
          onKeyDown={this.itemClickHandler} onClick={this.itemClickHandler} type="button" aria-label={this.getLabelCaption()}>
          {this.renderInstagramIcon(["hover-animation-2", "hover-animation-4"])}
          {this.imageUrl && this.imageHighResolutionUrl &&
          <img class="bg"
            onLoad={this.imageLoadedHandler} onError={this.imageLoadedHandler}
            srcset={`${this.imageUrl} 640w, ${this.imageHighResolutionUrl} 1080w`}
            sizes={this.imageResponsiveSizes}
            alt={this.altTag}
            loading="lazy" />
          }
        </button>
      </Host>
    );
  }

  private renderTextReviewItem() {
    const hideCaption = (this.caption === null) || this.caption.length === 0;
    const starRatingClass = this.rating > 0 ? "star-rating" : "";
    const starOnly = this.caption === null && this.itemType === "text";

    return (
      <Host class={{ [`${this.hoverAnimationStyle} hover-animation text-review`]: this.hoverEnabled, "hover-preview": this.appState.preview.hoverMode, "no-animation": this.animationDisabled, "highlighted": this.highlighted }}
        style={this.getItemCSSVariables()} aria-hidden={`${this.tabIndex === -1}`}>
        <button class="btn-item-wrap" disabled={this.tabIndex === -1} onFocus={this.itemOnFocusHandler} onMouseEnter={this.itemOnHoverHandler} 
          onKeyDown={this.itemClickHandler} onClick={this.itemClickHandler} type="button" aria-label={this.getLabelCaption()}>
          {this.hoverAnimationStyle === "hover-animation-5" && this.rating > 0 &&
          <StarRating rating={this.rating} ratingClass={`rating ${this.itemSize}`} label={`${this.rating}/5`}></StarRating>}
          <div class={`description text-review ${this.itemSize}`}>
            {this.rating > 0 && this.renderRating("text-review")}

            {/* text-review caption */}
            {!hideCaption && this.itemType === "text" &&
            <covet-pics-crop-text 
              text={this.caption}
              ratio={standardRatio}
            ></covet-pics-crop-text>
            }
            {starOnly &&
            <div class="star-wrap">
              <covet-pics-star-rating
                rating={this.rating}
                showLabel={false}>
              </covet-pics-star-rating>
            </div>
            }
            <span class={`info hover ${this.itemSize}`}>
              {this.renderInstagramIcon(["hover-animation-1", "hover-animation-5"])}
              {/* TODO && "show" */}
              <span class={`btn source-${this.itemSource} ${this.settings.item_hover_icon && "show"} ${starRatingClass}`}>
                {this.renderInstagramIcon(["hover-animation-3"])}

                {this.hoverAnimationStyle === "hover-animation-2" &&
                  <svg width="20" height="20" xmlns="http://www.w3.org/2000/svg" class="icon-arrow" aria-hidden="true"><path class="dynamic-fill" d="M13.3535534,4 L19.7071068,10.3535534 L13.3535534,16.7071068 L12.6464466,16 L17.792,10.853 L1,10.8535534 L1,9.85355339 L17.792,9.853 L12.6464466,4.70710678 L13.3535534,4 Z" fill="#ffffff" fill-rule="nonzero"></path></svg>
                }

                {this.getLabelCaption()}

                {this.hoverAnimationStyle === "hover-animation-4" && this.rating === 0 &&
                  <svg width="20px" height="20px" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><polygon fill="#fff" class="dynamic-fill" points="6 4.70710678 6.70710678 4 13.0606602 10.3535534 6.70710678 16.7071068 6 16 11.6464466 10.3535534"></polygon></svg>
                }
                {(this.hoverAnimationStyle === "hover-animation-3" || this.hoverAnimationStyle === "hover-animation-4") && this.rating > 0 && this.renderRating("info-rating")}
              </span>
            </span>
            {this.rating > 0 && this.renderUserAndDate()}
          </div>
          <span class="overlay-effect text-review"></span>
        </button>
      </Host>
    );
  }

  private renderDisabledHoveTextReviewItem() {
    const hideCaption = (this.caption === null) || this.caption.length === 0;
    const starOnly = this.caption === null && this.itemType === "text";

    return (
      <Host class={{ [`text-review ${this.hoverAnimationStyle}`]: true, "no-animation": true, "highlighted": this.highlighted }}
        style={this.getItemCSSVariables()} aria-hidden={`${this.tabIndex === -1}`}>
        <button class="btn-item-wrap" disabled={this.tabIndex === -1} onFocus={this.itemOnFocusHandler} onMouseEnter={this.itemOnHoverHandler} 
          onKeyDown={this.itemClickHandler} onClick={this.itemClickHandler} type="button" aria-label={this.getLabelCaption()}>
          <div class={`description text-review ${this.itemSize}`}>
            {this.rating > 0 && this.renderRating("text-review hover-disabled")}

            {/* text-review caption */}
            {!hideCaption && this.itemType === "text" &&
            <covet-pics-crop-text
              text={this.caption}
              ratio={standardRatio}
            ></covet-pics-crop-text>
            }
            {starOnly &&
              <div class="star-wrap">
                <covet-pics-star-rating
                  rating={this.rating}
                  showLabel={false}>
                </covet-pics-star-rating>
              </div>
            }
            {this.rating > 0 && this.renderUserAndDate()}
          </div>
        </button>
      </Host>
    );
  }

  private renderUploadWidget() {
    return (
      <Host class={{ "upload-widget": true, "no-animation": this.animationDisabled }} style={this.getItemCSSVariables()} onClick={this.itemClickHandler}
        onKeyDown={this.itemClickHandler} onfocus={this.itemOnFocusHandler} onMouseEnter={this.itemOnHoverHandler}
        tabindex={this.tabIndex} aria-hidden={`${this.tabIndex === -1}`}>
        <span class={`info ${this.itemSize}`}>
          {this.imageUrl === undefined &&
            <span class="default-cover">
              <svg class="svg-plus" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M24 0v24h24v1.2H24V48h-1.2V25.2H0V24h22.8V0H24z" fill="#464858" fill-rule="evenodd" /></svg>
              <span class="btn">{this.appState.settings.customer_photo_item_label}</span>
            </span>
          }

          {this.imageUrl !== undefined &&
            <div class="custom-img-wrap">
              <img class="img" src={this.imageUrl} alt="upload image" onLoad={this.imageLoadedHandler} onError={this.imageLoadedHandler} />
            </div>
          }
        </span>
      </Host>
    );
  }

  render() {
    if (this.itemSource === "upload_widget") return this.renderUploadWidget();
    if (this.itemType === "text" && this.settings.item_hover_disabled) return this.renderDisabledHoveTextReviewItem();
    if (this.itemType === "text") return this.renderTextReviewItem();
    if (!this.hoverEnabled) return this.renderDisabledHoverStandardItem();
    return this.renderStandardItem();
  }
}